import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AnalysisItemRequest,
  createAnalysis,
  deleteAnalysis,
  duplicateAnalysis,
  getAnalysisById,
  getAnalysisItems,
  getAnalysisList,
  getFilterSetForAnalysis,
  renameAnalysis,
  updateAnalysis,
} from '~apis/analysis';

export const AnalysisQueryKey = {
  default: () => ['analysis'],
  list: ({ companyId }: { companyId: string }) => [
    ...AnalysisQueryKey.default(),
    'list',
    { companyId },
  ],
  listByTableId: ({ companyId, id }: { companyId: string; id: number }) => [
    ...AnalysisQueryKey.list({ companyId }),
    'tableId',
    id,
  ],
  items: ({ ...query }: AnalysisItemRequest) => [...AnalysisQueryKey.default(), query, 'items'],
  filterSet: ({ companyId, tableId }: { companyId: string; tableId: number }) => [
    ...AnalysisQueryKey.default(),
    'filterSet',
    companyId,
    tableId,
  ],
};

export function useCreateAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAnalysis,
    onSuccess() {
      queryClient.invalidateQueries(AnalysisQueryKey.default());
    },
  });
}

export function useUpdateAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAnalysis,
    onSuccess() {
      queryClient.invalidateQueries(AnalysisQueryKey.default());
    },
  });
}

export function useDeleteAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAnalysis,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(AnalysisQueryKey.list({ companyId: variables.companyId }));
    },
  });
}

export function useDuplicateAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: duplicateAnalysis,
    onSuccess() {
      queryClient.invalidateQueries(AnalysisQueryKey.default());
    },
  });
}

export function useRenameAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: renameAnalysis,
    onSuccess() {
      queryClient.invalidateQueries(AnalysisQueryKey.default());
    },
  });
}

export function useGetAnalysisList({
  companyId,
  enabled,
}: {
  companyId: string;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: AnalysisQueryKey.list({
      companyId,
    }),
    enabled: !!companyId && enabled,
    queryFn: () => getAnalysisList({ companyId }),
  });
}

export function useGetAnalysisById({ companyId, id }: { companyId: string; id: number }) {
  return useQuery({
    queryKey: AnalysisQueryKey.listByTableId({ companyId, id }),
    queryFn: () => getAnalysisById({ companyId, id }),
    enabled: !!companyId && !!id,
    refetchOnMount: true,
  });
}

export function useGetAnalysisItems({ ...query }: AnalysisItemRequest) {
  return useQuery({
    queryKey: AnalysisQueryKey.items({ ...query }),
    queryFn: () => getAnalysisItems({ ...query }),
    enabled: !!query.companyId,
    refetchOnMount: true,
  });
}

export function useGetFilterSetForAnalysis({
  companyId,
  tableId,
}: {
  companyId: string;
  tableId: number;
}) {
  return useQuery({
    queryKey: AnalysisQueryKey.filterSet({ companyId, tableId }),
    queryFn: () => getFilterSetForAnalysis({ companyId, tableId }),
    enabled: !!companyId && !!tableId,
  });
}
